import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { Action as ReduxAction } from 'redux';
import * as _ from 'underscore';

import { ContentSpaceDefinitions} from '../../../ts/models/ContentDefinitions';
import { SpaceInfoItemType } from '../../../ts/models/ContentType';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { AppState } from '../../state/AppState';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { SpotlightAction, SpotlightActionButtons } from './SpotlightActionButtons';
import { SpaceDefinition } from '../../../ts/models/contentDefinitions/ContentDefinitionsTypes';

export function SpaceHeader<C extends SpaceInfoItemType>(props: SpaceHeaderProps<C>): JSX.Element {
  if (!props.item) {
    return null;
  }

  const item: ISpaceItemDetailInfo<C> = props.item;
  const customContainerClass = props.containerClassName || '';
  const showBasicName = _.isUndefined(props.nameElement);
  const nameElement =
    showBasicName?
      <h1 data-tb-test-id={SpaceHeaderTestIds.name} className={SpaceHeaderStyles.Title}>{item.name}</h1> :
      props.nameElement;

  return (
    <div>
      <div className={SpaceHeaderStyles.Row}
           data-tb-test-id={`tb-${item.contentType}-space-header-${item.id}`}>
        { !props.hideHeaderIcon && renderSpaceIcon(item) }
        <div className={`${SpaceHeaderStyles.ColumnFlexGrow} ${customContainerClass}`}>
          <div className={SpaceHeaderStyles.RowAlignedCenter}>
            { nameElement }
            { props.nameRowIcons }
          </div>
          <div>
            <h2 className={SpaceHeaderStyles.Metadata}>{ props.metadataRow }</h2>
          </div>
          { props.descriptionElement }
        </div>
      </div>
      { props.spotlightActions &&
          <SpotlightActionButtons actions={props.spotlightActions}
                                  item={item}
                                  availableActions={props.availableActions}
                                  dispatchSpotlightAction={props.dispatchSpotlightAction}/>
      }
      { props.bottomElement }
    </div>
  );
}

function renderSpaceIcon<C extends SpaceInfoItemType>(item: ISpaceItemDetailInfo<C>): JSX.Element {

  const spaceDefinition = ContentSpaceDefinitions[item.contentType] as unknown as SpaceDefinition<C>;

  return (
    <div className={SpaceHeaderStyles.IconDivWrapper}>
      <img src={spaceDefinition.headerIconPath(item)}
           alt={spaceDefinition.singularName()}
           className={SpaceHeaderStyles.Icon}/>
    </div>
  );
}

export interface SpaceHeaderDispatchProps {
  readonly dispatchSpotlightAction?: (reduxAction: ReduxAction) => void;
}

export interface SpaceHeaderOwnProps<C extends SpaceInfoItemType> {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly availableActions?: AvailableActions; // Required only if there are spotlight actions

  // if nameElement is undefined, this dipslays the item name as text. Otherwise, uses nameElement
  // to display the item name.
  readonly nameElement?: JSX.Element;
  readonly hideHeaderIcon?: boolean;

  // These elements are the variable portions of the header that can be specified differently
  // for each space type
  readonly nameRowIcons: JSX.Element | null;
  readonly metadataRow: JSX.Element;
  readonly descriptionElement?: JSX.Element | null;
  readonly spotlightActions?: SpotlightAction<C>[];
  readonly bottomElement?: JSX.Element;
  readonly containerClassName?: string;
}

export type SpaceHeaderProps<C extends SpaceInfoItemType> = SpaceHeaderDispatchProps & SpaceHeaderOwnProps<C>;

function mapDispatchToProps(dispatch: Dispatch<AppState>): SpaceHeaderDispatchProps {
  return {
    dispatchSpotlightAction: dispatch
  };
}

export const ConnectedSpaceHeader = connect(null, mapDispatchToProps)(SpaceHeader);

export namespace SpaceHeaderTestIds {
  export const name = 'space-header-name';
  export const ownerName = 'space-header-owner';
  export const hiddenOwnerName = 'space-header-hidden-owner';
  export const description = 'space-header-description';
  export const modifiedAt = 'space-header-modified-at';
  export const certifiedBy = 'space-header-certified-by';
  export const lastRefreshed = 'space-header-last-refreshed';
  export const readMore = 'space-header-read-more';
  export const dataQuality = 'space-header-data-quality';
  export const username = 'space-header-user-name';
  export const siterole = 'space-header-site-role';
  export const lastSignedIn = 'space-header-last-signed-in';
  export const email = 'space-header-email';
  export const dataStatus = 'space-header-data-status';
  export const runNextAt = 'space-header-run-next-at';
  export const domain = 'space-header-domain';
}
